import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { SuppliersModal } from '../Partials/SuppliersModal';

interface SuppliersModalControls {
  openSuppliersModal: () => void;
  closeSuppliersModal: () => void;
  isSuppliersModalOpen: boolean;
}

const SuppliersModalContext = createContext<SuppliersModalControls | undefined>(undefined);

/**
 * Hook to open the super-admin suppliers modal (per-org supplier access) from anywhere in the app.
 *
 * Mounted once at the root, like the feature flags modal. Non-super-admins get a no-op: the modal
 * renders nothing for them, so callers never need to gate on the role themselves.
 */
export const useSuppliersModal = (): SuppliersModalControls => {
  const context = useContext(SuppliersModalContext);
  if (!context) {
    throw new Error('useSuppliersModal must be used within a SuppliersModalProvider');
  }
  return context;
};

export const SuppliersModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openSuppliersModal = useCallback(() => setIsOpen(true), []);
  const closeSuppliersModal = useCallback(() => setIsOpen(false), []);

  const controls = useMemo(
    () => ({ openSuppliersModal, closeSuppliersModal, isSuppliersModalOpen: isOpen }),
    [openSuppliersModal, closeSuppliersModal, isOpen],
  );

  return (
    <SuppliersModalContext.Provider value={controls}>
      {children}
      <SuppliersModal visible={isOpen} onClose={closeSuppliersModal} />
    </SuppliersModalContext.Provider>
  );
};
